import { useGQLQuery } from "@hoppscotch/common/composables/graphql"
import { TeamName } from "@helpers/backend/types/TeamName"
import { getService } from "@hoppscotch/common/modules/dioc"
import { BackendPlatformDef } from "@hoppscotch/common/platform/backend"

import { runGQLQuery, runMutation, client } from "@helpers/backend/GQLClient"
import { HoppRESTRequest } from "@hoppscotch/data"
import { Email } from "@app/helpers/admin/Email"

import {
  AcceptTeamInvitationDocument,
  AcceptTeamInvitationMutation,
  AcceptTeamInvitationMutationVariables,
  CreateMockServerDocument,
  CreateMockServerMutation,
  CreateMockServerMutationVariables,
  CreatePublishedDocDocument,
  CreatePublishedDocsArgs,
  CreatePublishedDocMutation,
  CreatePublishedDocMutationVariables,
  CreateShortcodeDocument,
  CreateShortcodeMutation,
  CreateShortcodeMutationVariables,
  CreateTeamDocument,
  CreateTeamInvitationDocument,
  CreateTeamInvitationMutation,
  CreateTeamInvitationMutationVariables,
  CreateTeamMutation,
  CreateTeamMutationVariables,
  DeleteMockServerDocument,
  DeleteMockServerLogDocument,
  DeleteMockServerLogMutation,
  DeleteMockServerLogMutationVariables,
  DeleteMockServerMutation,
  DeleteMockServerMutationVariables,
  DeletePublishedDocDocument,
  DeletePublishedDocMutation,
  DeletePublishedDocMutationVariables,
  GetInviteDetailsDocument,
  GetInviteDetailsQuery,
  GetInviteDetailsQueryVariables,
  GetMockServerDocument,
  GetMockServerLogsDocument,
  GetMockServerLogsQuery,
  GetMockServerLogsQueryVariables,
  GetMockServerQuery,
  GetMockServerQueryVariables,
  GetMyMockServersDocument,
  GetMyMockServersQuery,
  GetMyMockServersQueryVariables,
  GetMyTeamsDocument,
  GetMyTeamsQuery,
  GetMyTeamsQueryVariables,
  GetTeamMockServersDocument,
  GetTeamMockServersQuery,
  GetTeamMockServersQueryVariables,
  GetUserShortcodesDocument,
  GetUserShortcodesQuery,
  GetUserShortcodesQueryVariables,
  PublishedDocDocument,
  PublishedDocQuery,
  PublishedDocQueryVariables,
  TeamMemberRole,
  TeamPublishedDocsListDocument,
  TeamPublishedDocsListQuery,
  TeamPublishedDocsListQueryVariables,
  UpdateMockServerDocument,
  UpdateMockServerMutation,
  UpdateMockServerMutationVariables,
  UpdatePublishedDocDocument,
  UpdatePublishedDocsArgs,
  UpdatePublishedDocMutation,
  UpdatePublishedDocMutationVariables,
  UserPublishedDocsListDocument,
  UserPublishedDocsListQuery,
  UserPublishedDocsListQueryVariables,
  WorkspaceType,
} from "@app/helpers/backend/graphql"
import { OrganizationService } from "@app/services/organization.service"
import * as TE from "fp-ts/TaskEither"
import * as E from "fp-ts/Either"

/**
 * Returns the current organization ID, or null on default cloud instances.
 */
const getOrgID = () => {
  const organizationService = getService(OrganizationService)
  return organizationService.getOrganizationID()
}

const getInviteDetails = <GetInviteDetailsError extends string>(
  inviteID: string
) => {
  return useGQLQuery<
    GetInviteDetailsQuery,
    GetInviteDetailsQueryVariables,
    GetInviteDetailsError
  >({
    query: GetInviteDetailsDocument,
    variables: {
      inviteID,
      orgID: getOrgID(),
    },
    defer: true,
  })
}

const getUserShortcodes = (cursor?: string) => {
  return runGQLQuery<
    GetUserShortcodesQuery,
    GetUserShortcodesQueryVariables,
    ""
  >({
    query: GetUserShortcodesDocument,
    variables: {
      cursor,
      orgID: getOrgID(),
    },
  })
}

const getUserTeams = (cursor?: string, matchAllTeams?: boolean) => {
  const orgID = getOrgID()

  return runGQLQuery<GetMyTeamsQuery, GetMyTeamsQueryVariables, "">({
    query: GetMyTeamsDocument,
    variables: {
      cursor,
      ...(matchAllTeams ? {} : { orgID }),
    },
  })
}

export const createTeam = <CreateTeamErrors extends string>(name: TeamName) => {
  return runMutation<
    CreateTeamMutation,
    CreateTeamMutationVariables,
    CreateTeamErrors
  >(CreateTeamDocument, {
    name,
    orgID: getOrgID(),
  })
}

export const createTeamInvitation = <CreateTeamInvitationErrors extends string>(
  inviteeEmail: Email,
  inviteeRole: TeamMemberRole,
  teamID: string
) => {
  return runMutation<
    CreateTeamInvitationMutation,
    CreateTeamInvitationMutationVariables,
    CreateTeamInvitationErrors
  >(CreateTeamInvitationDocument, {
    inviteeEmail,
    inviteeRole,
    teamID,
    orgID: getOrgID(),
  })
}

export const acceptTeamInvitation = <AcceptTeamInvitationErrors extends string>(
  inviteID: string
) => {
  return runMutation<
    AcceptTeamInvitationMutation,
    AcceptTeamInvitationMutationVariables,
    AcceptTeamInvitationErrors
  >(AcceptTeamInvitationDocument, {
    inviteID,
    orgID: getOrgID(),
  })
}

export const createShortcode = (
  request: HoppRESTRequest,
  properties?: string
) => {
  return runMutation<
    CreateShortcodeMutation,
    CreateShortcodeMutationVariables,
    ""
  >(CreateShortcodeDocument, {
    request: JSON.stringify(request),
    properties,
    orgID: getOrgID(),
  })
}

const createMockServer = (
  name: string,
  workspaceType?: WorkspaceType,
  workspaceID?: string,
  delayInMs?: number,
  isPublic?: boolean,
  collectionID?: string,
  autoCreateCollection?: boolean,
  autoCreateRequestExample?: boolean
) => {
  return TE.tryCatch(
    async () => {
      const result = await client
        .value!.mutation<
          CreateMockServerMutation,
          CreateMockServerMutationVariables
        >(CreateMockServerDocument, {
          input: {
            name,
            collectionID,
            autoCreateCollection,
            autoCreateRequestExample,
            workspaceType,
            workspaceID,
            delayInMs,
            isPublic,
          },
          orgID: getOrgID(),
        })
        .toPromise()

      if (result.error) {
        const err: any = result.error
        let message = err.message
        const gqlErr = (err.graphQLErrors && err.graphQLErrors[0]) || null
        if (gqlErr) {
          const orig = gqlErr.extensions?.originalError?.message
          if (orig) {
            message = Array.isArray(orig) ? orig.join(", ") : String(orig)
          } else if (gqlErr.message) {
            message = gqlErr.message
          }
        }
        throw new Error(message)
      }

      if (!result.data)
        throw new Error("No data returned from create mock server mutation")

      const data = result.data.createMockServer
      return {
        ...data,
        userUid: data.creator?.uid || "",
        collectionID: data.collection?.id || collectionID || "",
      }
    },
    (error) => (error as Error).message
  )
}

const updateMockServer = (
  id: string,
  input: {
    name?: string
    isActive?: boolean
    delayInMs?: number
    isPublic?: boolean
  }
) => {
  return TE.tryCatch(
    async () => {
      const result = await client
        .value!.mutation<
          UpdateMockServerMutation,
          UpdateMockServerMutationVariables
        >(UpdateMockServerDocument, { id, input, orgID: getOrgID() })
        .toPromise()

      if (result.error)
        throw new Error(result.error.message || "Failed to update mock server")
      if (!result.data)
        throw new Error("No data returned from update mock server mutation")

      const data = result.data.updateMockServer
      return {
        ...data,
        userUid: data.creator?.uid || "",
        collectionID: data.collection?.id || "",
      }
    },
    (error) => (error as Error).message
  )
}

const deleteMockServer = (id: string) => {
  return TE.tryCatch(
    async () => {
      const result = await client
        .value!.mutation<
          DeleteMockServerMutation,
          DeleteMockServerMutationVariables
        >(DeleteMockServerDocument, { id, orgID: getOrgID() })
        .toPromise()

      if (result.error)
        throw new Error(result.error.message || "Failed to delete mock server")
      if (!result.data)
        throw new Error("No data returned from delete mock server mutation")

      return result.data.deleteMockServer as boolean
    },
    (error) => (error as Error).message
  )
}

const getMockServer = (id: string) => {
  return TE.tryCatch(
    async () => {
      const result = await runGQLQuery<
        GetMockServerQuery,
        GetMockServerQueryVariables,
        ""
      >({
        query: GetMockServerDocument,
        variables: { id, orgID: getOrgID() },
      })

      if (E.isLeft(result)) throw result.left

      const data = result.right as GetMockServerQuery
      return {
        ...data.mockServer,
        userUid: data.mockServer.creator?.uid || "",
        collectionID: data.mockServer.collection?.id || "",
      }
    },
    (error) => error as string
  )
}

const getMyMockServers = (skip?: number, take?: number) => {
  return TE.tryCatch(
    async () => {
      const result = await runGQLQuery<
        GetMyMockServersQuery,
        GetMyMockServersQueryVariables,
        ""
      >({
        query: GetMyMockServersDocument,
        variables: { skip, take, orgID: getOrgID() },
      })

      if (E.isLeft(result)) throw result.left

      const data = result.right as GetMyMockServersQuery
      return data.myMockServers.map((mockServer) => ({
        ...mockServer,
        creator: mockServer.creator
          ? { uid: mockServer.creator.uid }
          : undefined,
        userUid: mockServer.creator?.uid || "",
        collectionID: mockServer.collection?.id || "",
      }))
    },
    (error) => error as string
  )
}

const getTeamMockServers = (teamID: string, skip?: number, take?: number) => {
  return TE.tryCatch(
    async () => {
      const result = await runGQLQuery<
        GetTeamMockServersQuery,
        GetTeamMockServersQueryVariables,
        ""
      >({
        query: GetTeamMockServersDocument,
        variables: { teamID, skip, take, orgID: getOrgID() },
      })

      if (E.isLeft(result)) throw result.left

      const data = result.right as GetTeamMockServersQuery
      return data.teamMockServers.map((mockServer) => ({
        ...mockServer,
        creator: mockServer.creator
          ? { uid: mockServer.creator.uid }
          : undefined,
        userUid: mockServer.creator?.uid || "",
        collectionID: mockServer.collection?.id || "",
      }))
    },
    (error) => error as string
  )
}

const getMockServerLogs = (
  mockServerID: string,
  skip?: number,
  take?: number
) => {
  return TE.tryCatch(
    async () => {
      const result = await client
        .value!.query<
          GetMockServerLogsQuery,
          GetMockServerLogsQueryVariables
        >(GetMockServerLogsDocument, { mockServerID, skip, take, orgID: getOrgID() })
        .toPromise()

      if (result.error)
        throw new Error(
          result.error.message || "Failed to fetch mock server logs"
        )
      if (!result.data) throw new Error("No data returned from mockServerLogs")

      return result.data.mockServerLogs
    },
    (e) => (e as Error).message
  )
}

const deleteMockServerLog = (logID: string) => {
  return TE.tryCatch(
    async () => {
      const result = await client
        .value!.mutation<
          DeleteMockServerLogMutation,
          DeleteMockServerLogMutationVariables
        >(DeleteMockServerLogDocument, { logID, orgID: getOrgID() })
        .toPromise()

      if (result.error)
        throw new Error(
          result.error.message || "Failed to delete mock server log"
        )
      if (!result.data)
        throw new Error("No data returned from deleteMockServerLog")

      return result.data.deleteMockServerLog as boolean
    },
    (e) => (e as Error).message
  )
}

const createPublishedDoc = (doc: CreatePublishedDocsArgs) => {
  return runMutation<
    CreatePublishedDocMutation,
    CreatePublishedDocMutationVariables,
    string
  >(CreatePublishedDocDocument, { args: doc, orgID: getOrgID() })
}

const updatePublishedDoc = (id: string, doc: UpdatePublishedDocsArgs) => {
  return runMutation<
    UpdatePublishedDocMutation,
    UpdatePublishedDocMutationVariables,
    string
  >(UpdatePublishedDocDocument, { id, args: doc, orgID: getOrgID() })
}

const deletePublishedDoc = (id: string) => {
  return runMutation<
    DeletePublishedDocMutation,
    DeletePublishedDocMutationVariables,
    string
  >(DeletePublishedDocDocument, { id, orgID: getOrgID() })
}

const getPublishedDocByID = (id: string) => {
  return TE.tryCatch(
    async () => {
      const result = await runGQLQuery<
        PublishedDocQuery,
        PublishedDocQueryVariables,
        ""
      >({
        query: PublishedDocDocument,
        variables: { id, orgID: getOrgID() },
      })

      if (E.isLeft(result)) throw result.left

      return (result.right as PublishedDocQuery).publishedDoc
    },
    (error) => {
      console.error("Error fetching published doc:", error)
      return "published_docs/not_found" as string
    }
  )
}

const getUserPublishedDocs = (skip: number = 0, take: number = 100) => {
  return TE.tryCatch(
    async () => {
      const result = await runGQLQuery<
        UserPublishedDocsListQuery,
        UserPublishedDocsListQueryVariables,
        ""
      >({
        query: UserPublishedDocsListDocument,
        variables: { skip, take, orgID: getOrgID() },
      })

      if (E.isLeft(result)) throw result.left

      return (result.right as UserPublishedDocsListQuery).userPublishedDocsList
    },
    (error) => error as string
  )
}

const getTeamPublishedDocs = (
  teamID: string,
  collectionID?: string,
  skip: number = 0,
  take: number = 100
) => {
  return TE.tryCatch(
    async () => {
      const result = await runGQLQuery<
        TeamPublishedDocsListQuery,
        TeamPublishedDocsListQueryVariables,
        ""
      >({
        query: TeamPublishedDocsListDocument,
        variables: { teamID, collectionID, skip, take, orgID: getOrgID() },
      })

      if (E.isLeft(result)) throw result.left

      return (result.right as TeamPublishedDocsListQuery).teamPublishedDocsList
    },
    (error) => error as string
  )
}

export const def: BackendPlatformDef = {
  // Team & invite operations
  getInviteDetails,
  getUserShortcodes,
  getUserTeams,
  createTeam,
  createTeamInvitation,
  acceptTeamInvitation,
  createShortcode,

  // Mock server operations
  createMockServer,
  updateMockServer,
  deleteMockServer,
  getMockServer,
  getMyMockServers,
  getTeamMockServers,
  getMockServerLogs,
  deleteMockServerLog,

  // Published docs operations
  createPublishedDoc,
  updatePublishedDoc,
  deletePublishedDoc,
  getPublishedDocByID,
  getUserPublishedDocs,
  getTeamPublishedDocs,
}
